import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Plus, Edit2, Trash2, Eye, MoreVertical, FileText, Tag, User, Calendar, Sparkles, Loader2, Image as ImageIcon, Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function Blogs() {
  const { isDark } = useTheme();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [viewBlog, setViewBlog] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: 'Aurous Team',
    category: '',
    tags: [],
    status: 'draft',
    featured_image: '',
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [blogs, searchTerm, statusFilter]);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const filterBlogs = () => {
    let filtered = [...blogs];

    if (searchTerm) {
      filtered = filtered.filter(
        (blog) =>
          blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          blog.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
          blog.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((blog) => blog.status === statusFilter);
    }

    setFilteredBlogs(filtered);
  };

  const generateAIImage = async () => {
    if (!formData.title) {
      toast.error('Please enter a blog title first');
      return;
    }

    setGeneratingImage(true);
    try {
      const response = await axios.post(`${API}/generate-blog-image`, {
        title: formData.title,
        content: formData.content || formData.excerpt,
        style: 'professional, modern, tech, AI, corporate'
      });

      if (response.data.image_base64) {
        const imageUrl = `data:image/png;base64,${response.data.image_base64}`;
        setFormData(prev => ({ ...prev, featured_image: imageUrl }));
        toast.success('AI image generated successfully!');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateAIContent = async () => {
    if (!formData.title) {
      toast.error('Please enter a blog title first');
      return;
    }

    setGeneratingContent(true);
    try {
      const response = await axios.post(`${API}/generate-blog-content`, {
        title: formData.title,
        category: formData.category || 'Technology',
        tone: 'professional'
      });

      if (response.data) {
        setFormData(prev => ({
          ...prev,
          content: response.data.content || prev.content,
          excerpt: response.data.excerpt || prev.excerpt,
          tags: response.data.suggested_tags || prev.tags
        }));
        toast.success('AI content generated successfully!');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI content');
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.excerpt || !formData.content || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingBlog) {
        await axios.put(`${API}/blogs/${editingBlog.id}`, formData);
        setBlogs((prev) =>
          prev.map((blog) => (blog.id === editingBlog.id ? { ...blog, ...formData } : blog))
        );
        toast.success('Blog updated successfully');
      } else {
        const response = await axios.post(`${API}/blogs`, formData);
        setBlogs((prev) => [response.data, ...prev]);
        toast.success('Blog created successfully');
      }
      closeDialog();
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error('Failed to save blog');
    }
  };

  const deleteBlog = async (blogId) => {
    try {
      await axios.delete(`${API}/blogs/${blogId}`);
      setBlogs((prev) => prev.filter((blog) => blog.id !== blogId));
      toast.success('Blog deleted');
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Failed to delete blog');
    }
  };

  const togglePublish = async (blog) => {
    const newStatus = blog.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/blogs/${blog.id}`, { status: newStatus });
      setBlogs((prev) =>
        prev.map((b) => (b.id === blog.id ? { ...b, status: newStatus } : b))
      );
      toast.success(`Blog ${newStatus === 'published' ? 'published' : 'unpublished'}`);
    } catch (error) {
      console.error('Error updating blog:', error);
      toast.error('Failed to update blog');
    }
  };

  const openEditDialog = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      excerpt: blog.excerpt,
      content: blog.content,
      author: blog.author,
      category: blog.category,
      tags: blog.tags || [],
      status: blog.status,
      featured_image: blog.featured_image || '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingBlog(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: 'Aurous Team',
      category: '',
      tags: [],
      status: 'draft',
      featured_image: '',
    });
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter((tag) => tag !== tagToRemove) });
  };

  const statusColors = {
    draft: 'bg-gray-100 text-gray-700 border-gray-200',
    published: 'bg-green-100 text-green-700 border-green-200',
  };

  const categories = ['Career Tips', 'Interview Tips', 'Work Culture', 'Industry News', 'Technology', 'Company Updates'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="blogs-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blogs</h1>
          <p className="text-slate-500 mt-1">Create and manage blog posts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => closeDialog()} data-testid="add-blog-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Blog Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</DialogTitle>
              <DialogDescription>
                {editingBlog ? 'Update blog post details' : 'Write a new blog post'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Enter blog title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="blog-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="author">Author</Label>
                  <Input
                    id="author"
                    placeholder="Author name"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    data-testid="blog-author-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="blog-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="excerpt">Excerpt *</Label>
                <Textarea
                  id="excerpt"
                  placeholder="Brief summary of the blog post"
                  rows={2}
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  data-testid="blog-excerpt-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="content">Content *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateAIContent}
                    disabled={generatingContent || !formData.title}
                    className="gap-1"
                  >
                    {generatingContent ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3" />
                        AI Generate Content
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  id="content"
                  placeholder="Write your blog content here or use AI to generate..."
                  rows={8}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  data-testid="blog-content-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    data-testid="tag-input"
                  />
                  <Button type="button" variant="outline" onClick={addTag}>
                    Add
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="cursor-pointer hover:bg-red-100"
                        onClick={() => removeTag(tag)}
                      >
                        {tag} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="blog-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="featured_image">Featured Image</Label>
                  <div className="flex gap-2">
                    <Input
                      id="featured_image"
                      placeholder="URL or generate with AI"
                      value={formData.featured_image}
                      onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                      data-testid="featured-image-input"
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={generateAIImage}
                      disabled={generatingImage || !formData.title}
                      className="flex items-center gap-2 whitespace-nowrap"
                    >
                      {generatingImage ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          AI Image
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Image Preview */}
              {formData.featured_image && (
                <div className="space-y-2">
                  <Label>Image Preview</Label>
                  <div className="relative rounded-lg overflow-hidden border bg-slate-50">
                    <img 
                      src={formData.featured_image} 
                      alt="Featured" 
                      className="w-full h-48 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({ ...formData, featured_image: '' })}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-blog-btn">
                  {editingBlog ? 'Update' : 'Create'} Post
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search blogs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-blogs"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="blog-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Posts</SelectItem>
                <SelectItem value="draft">Drafts</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Blogs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBlogs.map((blog) => (
          <Card key={blog.id} className="border-0 shadow-md hover:shadow-lg transition-shadow overflow-hidden">
            {blog.featured_image && (
              <div className="h-40 bg-slate-100 overflow-hidden">
                <img
                  src={blog.featured_image}
                  alt={blog.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <Badge className={cn('capitalize text-xs mb-2', statusColors[blog.status])}>
                    {blog.status}
                  </Badge>
                  <CardTitle className="text-lg line-clamp-2">{blog.title}</CardTitle>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`blog-actions-${blog.id}`}>
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { setViewBlog(blog); setViewDialogOpen(true); }}>
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEditDialog(blog)}>
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => togglePublish(blog)}>
                      <FileText className="w-4 h-4 mr-2" />
                      {blog.status === 'published' ? 'Unpublish' : 'Publish'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => deleteBlog(blog.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 line-clamp-2 mb-4">{blog.excerpt}</p>

              <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {blog.author}
                </span>
                <span className="flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  {blog.category}
                </span>
              </div>

              {blog.tags && blog.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {blog.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {blog.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{blog.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between mt-4 pt-4 border-t text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(blog.created_at).toLocaleDateString()}
                </span>
                <span>{blog.views || 0} views</span>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredBlogs.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-12">
            <div className="p-4 rounded-full bg-slate-100 mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-500">No blog posts found</p>
          </div>
        )}
      </div>

      {/* View Blog Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Blog Preview</DialogTitle>
          </DialogHeader>
          {viewBlog && (
            <div className="space-y-4">
              {viewBlog.featured_image && (
                <div className="h-48 bg-slate-100 rounded-lg overflow-hidden">
                  <img
                    src={viewBlog.featured_image}
                    alt={viewBlog.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <Badge className={cn('capitalize mb-2', statusColors[viewBlog.status])}>
                  {viewBlog.status}
                </Badge>
                <h2 className="text-2xl font-bold">{viewBlog.title}</h2>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span>By {viewBlog.author}</span>
                <span>•</span>
                <span>{viewBlog.category}</span>
                <span>•</span>
                <span>{new Date(viewBlog.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-slate-600 italic border-l-4 border-blue-500 pl-4">{viewBlog.excerpt}</p>
              <div className="prose prose-slate max-w-none">
                <p>{viewBlog.content}</p>
              </div>
              {viewBlog.tags && viewBlog.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {viewBlog.tags.map((tag) => (
                    <Badge key={tag} variant="secondary">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
